import {
  forwardRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { springs } from "@andrum/design-tokens";
import { cn } from "../lib/cn";

/**
 * Headspace-inspired cards: soft, spacious, rounded, light elevation.
 * Padding is intentionally generous so content never feels edge-stuck.
 */
const cardVariants = cva(
  [
    "rounded-[24px] text-foreground",
    "border border-border/55",
    "bg-surface-raised/90 backdrop-blur-sm",
    "shadow-[0_8px_28px_rgb(40_35_28_/_0.06),0_1px_2px_rgb(40_35_28_/_0.03)]",
  ].join(" "),
  {
    variants: {
      padding: {
        none: "p-0",
        sm: "p-5",
        md: "p-6 sm:p-7",
        lg: "p-7 sm:p-9",
      },
      elevation: {
        flat: "shadow-none border-border/40 bg-surface-raised/70",
        raised: "",
        floating:
          "shadow-[0_18px_50px_rgb(40_35_28_/_0.1),0_4px_12px_rgb(40_35_28_/_0.04)] border-border/45",
      },
      interactive: {
        true: "cursor-pointer",
        false: "",
      },
    },
    defaultVariants: {
      padding: "md",
      elevation: "raised",
      interactive: false,
    },
  }
);

export type CardProps = {
  children?: ReactNode;
  className?: string;
  padding?: VariantProps<typeof cardVariants>["padding"];
  elevation?: VariantProps<typeof cardVariants>["elevation"];
  interactive?: boolean;
  asButton?: boolean;
  id?: string;
  "aria-labelledby"?: string;
  "aria-label"?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    className,
    padding = "md",
    elevation = "raised",
    interactive = false,
    asButton = false,
    children,
    onClick,
    id,
    "aria-labelledby": ariaLabelledby,
    "aria-label": ariaLabel,
  },
  ref
) {
  const reducedMotion = useReducedMotion();
  const isInteractive = interactive || asButton || Boolean(onClick);

  if (isInteractive) {
    return (
      <motion.div
        ref={ref}
        id={id}
        role={asButton ? "button" : undefined}
        tabIndex={asButton ? 0 : undefined}
        aria-labelledby={ariaLabelledby}
        aria-label={ariaLabel}
        className={cn(
          cardVariants({
            padding,
            elevation,
            interactive: true,
          }),
          className
        )}
        {...(reducedMotion
          ? {}
          : {
              whileTap: { scale: 0.988 },
              whileHover: { y: -2, transition: springs.soft },
            })}
        onClick={onClick}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          if (!asButton || !onClick) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick(event as unknown as MouseEvent<HTMLDivElement>);
          }
        }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <div
      ref={ref}
      id={id}
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
      className={cn(
        cardVariants({ padding, elevation, interactive: false }),
        className
      )}
    >
      {children}
    </div>
  );
});

export function CardHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mb-5 space-y-2", className)} {...props} />;
}

export function CardTitle({
  className,
  as: Tag = "h3",
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { as?: "h2" | "h3" | "h4" | "p" }) {
  return (
    <Tag
      className={cn(
        "text-xl font-semibold tracking-tight text-foreground [font-family:var(--font-display)]",
        className
      )}
      {...props}
    />
  );
}

export function CardDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn("text-[15px] leading-relaxed text-foreground-muted", className)}
      {...props}
    />
  );
}

export { cardVariants };
